
import Compounds from 'modules/compounds/Compounds';
import ConceptLinker from 'modules/conceptLinker/ConceptLinker';
import CulturalVoc from 'modules/Culturalvocs/CulturalVocs';
import Glossary from 'modules/glossary/Glossaries';
import Inflects from 'modules/inflects/Inflects';
import PanelIntro from 'modules/panelIntro/PanelIntro';
import Panels from 'modules/panels/Panels';
import PanelsFactory from 'modules/panelsFactory/PanelsFactory';
import Sentence from 'modules/sentence/Sentence';
import SourceAdaptation from 'modules/sourceAdaptation/SourceAdaptation';
import React from 'react';
import styled from 'styled-components';
import { IModuleRequest } from 'types/module';
import { IRefIds } from '../constants/refs';
import { createModuleNavigationKey } from 'helpers/navigationHelpers';
import Admin from './Admin/Admin';
import Course from './Courses/Course';
import Dialog from './Dialog/Dialog';
import Exercise from './Exercise/Exercise';
import Heading from './Heading/Heading';
import Items from './items/Items';
import KLine from './kLine/Kline';
import LessonOutline from './LessonOutline/LessonOutline';
import LessonTitle from './LessonTitle/LessonTitle';
import PanelTitle from './PanelTitle/PanelTitle';
import Reading from './Reading/Reading';
import Segment from './Segment/Segment';
import Vocabulary from './vocabulary/Vocabulary';
import Audio from './Audio/Audio';
import Conjugation from './Conjugation/Conjugation';
import Languages from './Languages/Languages';
import TableLiterals from './TableLiterals/TableLiterals';
import Table from './Table/Table';
import Chart from './Chart/Chart';
import CAT from './CAT/CAT';
import Feedback from './Feedback/Feedback';
import Book from './Book/Book';
import Trainer from './Trainer/Trainer';
import TtsLab from './TtsLab/TtsLab';
import VocCategory from './VocCategory/VocCategory';
import Memory from './Memory/Memory';
import ConceptLinkerValidator from './conceptLinkerValidator/ConceptLinkerValidator';
import Health from './Health/Health';
import Config from './Config/Config';
import AgentQA from './AgentQA/AgentQA';
import AIIllustration from './AIIllustration/AIIllustration';
import AiAssistant from './AiAssistant/AiAssistant';
import KnowledgeBase from './KnowledgeBase/KnowledgeBase';
import TranslationUnified from './TranslationUnified/TranslationUnified';
// Lazy: its templates embed ~8 MB of Figma art that no other module needs
const Screenshots = React.lazy(() => import('./Screenshots/Screenshots'));
import ConceptTree from './ConceptTree/ConceptTree';
import Hanzi from './Hanzi/Hanzi';

type IProps = {
  module: IModuleRequest;
  isActive: boolean;
};

export const Module: React.FC<IProps> = (props) => {
  const { module } = props;
  const { courseId, search, dialect, navigationPath } = module;

  const renderModule = () => {
    switch (module.refId) {
      case IRefIds.admin:
        return <Admin />;

      case IRefIds.screenshots:
        return (
          <React.Suspense fallback={null}>
            <Screenshots />
          </React.Suspense>
        );

      case IRefIds.hanzi:
        return <Hanzi />;

      case IRefIds.courses:
        return <Course />;

      case IRefIds.languages:
        return <Languages />;

      case IRefIds.heading:
        return <Heading />;

      case IRefIds.ttsLab:
        return <TtsLab />;

      case IRefIds.vocabularyCategories:
        return <VocCategory />;

      case IRefIds.klines:
        return <KLine courseId={courseId} />;

      case IRefIds.exercise:
        return <Exercise courseId={courseId} search={search} />;

      case IRefIds.panelIntro:
        return <PanelIntro courseId={courseId} />;

      case IRefIds.glossaries:
        return <Glossary courseId={courseId} search={search} />;

      case IRefIds.cultural:
        return <CulturalVoc courseId={courseId} search={search} />;

      case IRefIds.panels:
        return <Panels courseId={courseId} search={search} />;

      case IRefIds.section:
        return <Inflects courseId={courseId} />;

      case IRefIds.items:
        return <Items courseId={courseId} search={search} />;

      case IRefIds.compounds:
        return <Compounds courseId={courseId} search={search} />;

      case IRefIds.sentences:
        return <Sentence courseId={courseId} search={search} />;

      case IRefIds.panelsFactory:
        return <PanelsFactory courseId={courseId} />;

      case IRefIds.conceptLinker:
        return <ConceptLinker courseId={courseId} />;

      case IRefIds.conceptTree:
        if (courseId !== undefined) {
          return <ConceptTree courseId={courseId} />;
        }
        return <span>Missing module data</span>;


      case IRefIds.sourceAdaptation:
        return <SourceAdaptation courseId={courseId} search={search} />;

      case IRefIds.vocs:
      case IRefIds.vocsCourse:
        return <Vocabulary courseId={courseId} search={search} />;

      case IRefIds.segment:
        if (courseId !== undefined) {
          return <Segment courseId={courseId} search={search} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.lessons:
      case IRefIds.lessonOutline:
        if (courseId !== undefined) {
          return <LessonOutline courseId={courseId} search={search} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.dialog:
        if (courseId !== undefined) {
          const key = createModuleNavigationKey('dialog', courseId, search, navigationPath);
          return <Dialog key={key} courseId={courseId} search={search} navigationPath={navigationPath} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.panelTitle:
        if (courseId !== undefined) {
          return <PanelTitle courseId={courseId} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.lessonTitle:
        if (courseId !== undefined) {
          return <LessonTitle courseId={courseId} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.reading:
        if (courseId !== undefined) {
          const key = createModuleNavigationKey('reading', courseId, search, navigationPath);
          return <Reading key={key} courseId={courseId} search={search} navigationPath={navigationPath} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.audio:
        if (courseId !== undefined) {
          return <Audio courseId={courseId} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.health:
        return <Health />;

      case IRefIds.config:
        return <Config />;

      case IRefIds.conjugation:
        if (courseId !== undefined) {
          return <Conjugation courseId={courseId} search={search} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.tableLiterals:
        if (courseId !== undefined) {
          return <TableLiterals courseId={courseId} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.table:
        if (courseId !== undefined) {
          return <Table courseId={courseId} search={search} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.chart:
        if (courseId !== undefined) {
          return <Chart courseId={courseId} search={search} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.SDL:
        if (courseId !== undefined) {
          return <CAT courseId={courseId} search={search} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.feedback:
        if (courseId !== undefined) {
          return <Feedback courseId={courseId} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.book:
        if (courseId !== undefined) {
          const key = createModuleNavigationKey('book', courseId, search, navigationPath);
          return <Book key={key} courseId={courseId} search={search} navigationPath={navigationPath} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.trainer:
        if (courseId !== undefined) {
          const key = createModuleNavigationKey('trainer', courseId, search, navigationPath);
          return <Trainer key={key} courseId={courseId} search={search} navigationPath={navigationPath} />;
        }
        return <span>Missing module data</span>;

      case IRefIds.memory:
        return <Memory search={search} dialect={dialect} subModuleId={module.subModuleId} />;

      case IRefIds.conceptLinkerValidator:
        return <ConceptLinkerValidator />;

      case IRefIds.agentQA:
        return <AgentQA />;

      case IRefIds.aiIllustration:
        return <AIIllustration />;

      case IRefIds.aiAssistant:
        return <AiAssistant />;

      case IRefIds.knowledgeBase:
        return <KnowledgeBase articleId={search ? Number(search) : undefined} />;

      case IRefIds.translationUnified:
        return <TranslationUnified />;

      default:
        return <span>Under construction</span>;
    }
  };
  return <ModuleContainer $isActive={props.isActive}>{renderModule()}</ModuleContainer>;
};

const ModuleContainer = styled.div<{ $isActive: boolean }>`
  display: ${(props) => (props.$isActive ? 'block' : 'none')};
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 0;
`;
